/**
 * Credit Representative Detail Page — F006
 *
 * Tabbed detail view with:
 *  - Profile tab: CR fields read-only, edit modal
 *  - Documents tab: CR documents table, add/edit/delete
 *  - File Reviews tab: file reviews table, schedule/complete/delete
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { format } from "date-fns";
import { ArrowLeft, Edit, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceLayout } from "./compliance-layout";
import { CreditRepModal } from "@/components/modals/credit-rep-modal";
import { CrDocumentModal } from "@/components/modals/cr-document-modal";
import { CrFileReviewModal } from "@/components/modals/cr-file-review-modal";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CrDocument {
  id: string;
  creditRepresentativeId: string;
  documentType: string;
  documentName: string;
  documentStatus: string;
  fileUrl: string | null;
  filePath: string | null;
  effectiveDate: string | null;
  expiryDate: string | null;
  versionNumber: number | null;
  createdAt: string;
}

interface CrFileReview {
  id: string;
  creditRepresentativeId: string;
  reviewType: string;
  scheduledDate: string;
  reviewStatus: string;
  reviewOutcome: string | null;
  findings: string | null;
  actionsRequired: string | null;
  dealId: string | null;
  reviewerId: string | null;
  createdAt: string;
}

interface CreditRepDetail {
  id: string;
  entityName: string;
  crNumber: string | null;
  appointmentType: string;
  entityType: string | null;
  abn: string | null;
  acn: string | null;
  crStatus: string;
  complianceStatus: string;
  appointmentDate: string | null;
  cessationDate: string | null;
  conditions: string | null;
  supervisorId: string | null;
  supervisionLevel: string | null;
  piPolicyReference: string | null;
  piExpiryDate: string | null;
  cpdHoursRequired: number | null;
  cpdHoursCompleted: number | null;
  createdAt: string;
  updatedAt: string | null;
  documents: CrDocument[];
  fileReviews: CrFileReview[];
}

type TabKey = "profile" | "documents" | "file-reviews";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CR_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-green-100 text-green-700 border-green-300" },
  suspended: { label: "Suspended", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  terminated: { label: "Terminated", className: "bg-red-100 text-red-700 border-red-300" },
  pending: { label: "Pending", className: "bg-slate-100 text-slate-700 border-slate-300" },
};

const COMPLIANCE_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  compliant: { label: "Compliant", className: "bg-green-100 text-green-700 border-green-300" },
  non_compliant: { label: "Non-Compliant", className: "bg-red-100 text-red-700 border-red-300" },
  under_review: { label: "Under Review", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  remediation_required: { label: "Remediation Required", className: "bg-orange-100 text-orange-700 border-orange-300" },
};

const DOCUMENT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-slate-100 text-slate-700 border-slate-300" },
  active: { label: "Active", className: "bg-green-100 text-green-700 border-green-300" },
  superseded: { label: "Superseded", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  archived: { label: "Archived", className: "bg-gray-100 text-gray-600 border-gray-300" },
};

const REVIEW_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  scheduled: { label: "Scheduled", className: "bg-blue-100 text-blue-700 border-blue-300" },
  in_progress: { label: "In Progress", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700 border-green-300" },
  cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-600 border-gray-300" },
};

const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  full: "Full",
  limited: "Limited",
  provisional: "Provisional",
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  appointment: "Appointment",
  authority: "Authority",
  training: "Training",
  compliance: "Compliance",
  termination: "Termination",
  other: "Other",
};

const REVIEW_TYPE_LABELS: Record<string, string> = {
  spot_check: "Spot Check",
  random: "Random",
  targeted: "Targeted",
  complaint_triggered: "Complaint Triggered",
  annual: "Annual",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Badge components
// ---------------------------------------------------------------------------

function CrStatusBadge({ status }: { status: string }) {
  const config = CR_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

function ComplianceStatusBadge({ status }: { status: string }) {
  const config = COMPLIANCE_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

function DocumentStatusBadge({ status }: { status: string }) {
  const config = DOCUMENT_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

function ReviewStatusBadge({ status }: { status: string }) {
  const config = REVIEW_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Profile read-only field helper
// ---------------------------------------------------------------------------

function ProfileField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value ?? "-"}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile Tab
// ---------------------------------------------------------------------------

function ProfileTab({
  cr,
  onEdit,
}: {
  cr: CreditRepDetail;
  onEdit: () => void;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Appointment Details</CardTitle>
          <Button
            variant="outline"
            size="sm"
            data-testid="button-edit-cr"
            onClick={onEdit}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3">
            <ProfileField label="Entity Name" value={cr.entityName} />
            <ProfileField label="CR Number" value={cr.crNumber} />
            <ProfileField
              label="Appointment Type"
              value={APPOINTMENT_TYPE_LABELS[cr.appointmentType] ?? cr.appointmentType}
            />
            <ProfileField
              label="Entity Type"
              value={cr.entityType ? (cr.entityType.charAt(0).toUpperCase() + cr.entityType.slice(1)) : null}
            />
            <ProfileField label="ABN" value={cr.abn} />
            <ProfileField label="ACN" value={cr.acn} />
            <ProfileField label="Appointment Date" value={formatDate(cr.appointmentDate)} />
            <ProfileField label="Cessation Date" value={formatDate(cr.cessationDate)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">CR Status</p>
              <CrStatusBadge status={cr.crStatus} />
            </div>
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">Compliance Status</p>
              <ComplianceStatusBadge status={cr.complianceStatus} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Supervision & Insurance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3">
            <ProfileField label="Supervision Level" value={cr.supervisionLevel} />
            <ProfileField label="PI Policy Reference" value={cr.piPolicyReference} />
            <ProfileField label="PI Expiry Date" value={formatDate(cr.piExpiryDate)} />
            <ProfileField
              label="CPD Hours Required"
              value={cr.cpdHoursRequired != null ? String(cr.cpdHoursRequired) : null}
            />
            <ProfileField
              label="CPD Hours Completed"
              value={cr.cpdHoursCompleted != null ? String(cr.cpdHoursCompleted) : null}
            />
          </div>
        </CardContent>
      </Card>

      {cr.conditions && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{cr.conditions}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents Tab
// ---------------------------------------------------------------------------

function DocumentsTab({ cr }: { cr: CreditRepDetail }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isAddDocModalOpen, setIsAddDocModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<CrDocument | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  const deleteDocMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/compliance/cr-documents/${id}`
      );
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to delete document");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${cr.id}`],
      });
      toast({ title: "Document deleted" });
      setDeletingDocId(null);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
      setDeletingDocId(null);
    },
  });

  const documents = cr.documents ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          data-testid="button-add-document"
          onClick={() => setIsAddDocModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Document
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Effective Date</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-8 text-center text-muted-foreground"
                >
                  No documents uploaded. Add your first document.
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id} data-testid={`row-document-${doc.id}`}>
                  <TableCell>
                    {DOCUMENT_TYPE_LABELS[doc.documentType] ?? doc.documentType}
                  </TableCell>
                  <TableCell className="font-medium">{doc.documentName}</TableCell>
                  <TableCell>
                    <DocumentStatusBadge status={doc.documentStatus} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(doc.effectiveDate)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(doc.expiryDate)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {doc.fileUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`button-view-doc-${doc.id}`}
                          asChild
                        >
                          <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`button-edit-doc-${doc.id}`}
                        onClick={() => setEditingDoc(doc)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`button-delete-doc-${doc.id}`}
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeletingDocId(doc.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add Document Modal */}
      <CrDocumentModal
        open={isAddDocModalOpen}
        onOpenChange={setIsAddDocModalOpen}
        creditRepId={cr.id}
        onSuccess={() => setIsAddDocModalOpen(false)}
      />

      {/* Edit Document Modal */}
      <CrDocumentModal
        open={!!editingDoc}
        onOpenChange={(open) => {
          if (!open) setEditingDoc(null);
        }}
        creditRepId={cr.id}
        editingDocument={editingDoc}
        onSuccess={() => setEditingDoc(null)}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingDocId}
        onOpenChange={(open) => {
          if (!open) setDeletingDocId(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete-doc">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-doc"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deletingDocId) deleteDocMutation.mutate(deletingDocId);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File Reviews Tab
// ---------------------------------------------------------------------------

function FileReviewsTab({ cr }: { cr: CreditRepDetail }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [completingReview, setCompletingReview] = useState<CrFileReview | null>(null);
  const [deletingReviewId, setDeletingReviewId] = useState<string | null>(null);

  const deleteReviewMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/compliance/file-reviews/${id}`
      );
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to delete file review");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${cr.id}`],
      });
      toast({ title: "File review deleted" });
      setDeletingReviewId(null);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
      setDeletingReviewId(null);
    },
  });

  const reviews = cr.fileReviews ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          data-testid="button-schedule-review"
          onClick={() => setIsScheduleModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Schedule Review
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Scheduled Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-8 text-center text-muted-foreground"
                >
                  No file reviews scheduled.
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((review) => (
                <TableRow key={review.id} data-testid={`row-review-${review.id}`}>
                  <TableCell>
                    {REVIEW_TYPE_LABELS[review.reviewType] ?? review.reviewType}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(review.scheduledDate)}
                  </TableCell>
                  <TableCell>
                    <ReviewStatusBadge status={review.reviewStatus} />
                  </TableCell>
                  <TableCell>
                    {review.reviewOutcome ? (
                      <span className="text-sm capitalize">
                        {review.reviewOutcome.replace(/_/g, " ")}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {review.reviewStatus !== "completed" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`button-complete-review-${review.id}`}
                          onClick={() => setCompletingReview(review)}
                        >
                          Complete
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`button-edit-review-${review.id}`}
                        onClick={() => setCompletingReview(review)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`button-delete-review-${review.id}`}
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeletingReviewId(review.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Schedule Review Modal */}
      <CrFileReviewModal
        open={isScheduleModalOpen}
        onOpenChange={setIsScheduleModalOpen}
        creditRepId={cr.id}
        scheduleMode
        onSuccess={() => setIsScheduleModalOpen(false)}
      />

      {/* Complete/Edit Review Modal */}
      <CrFileReviewModal
        open={!!completingReview}
        onOpenChange={(open) => {
          if (!open) setCompletingReview(null);
        }}
        creditRepId={cr.id}
        editingReview={completingReview}
        onSuccess={() => setCompletingReview(null)}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingReviewId}
        onOpenChange={(open) => {
          if (!open) setDeletingReviewId(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete-review">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File Review</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this file review? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-review"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deletingReviewId) deleteReviewMutation.mutate(deletingReviewId);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main detail component
// ---------------------------------------------------------------------------

function CreditRepDetailContent() {
  const [, params] = useRoute("/compliance/credit-reps/:id");
  const [, setLocation] = useLocation();

  const crId = params?.id ?? "";

  const [activeTab, setActiveTab] = useState<TabKey>("profile");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data, isLoading, isError } = useQuery<CreditRepDetail>({
    queryKey: [`/api/compliance/credit-reps/${crId}`],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/compliance/credit-reps/${crId}`
      );
      if (!response.ok) {
        throw new Error("Failed to load credit representative");
      }
      return response.json();
    },
    enabled: !!crId,
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-5xl">
        <div className="text-center py-12 text-muted-foreground">
          <p>Credit representative not found.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => setLocation("/compliance/credit-reps")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to list
          </Button>
        </div>
      </div>
    );
  }

  const TABS: { key: TabKey; label: string; testId: string }[] = [
    { key: "profile", label: "Profile", testId: "tab-cr-profile" },
    { key: "documents", label: `Documents (${data.documents?.length ?? 0})`, testId: "tab-cr-documents" },
    { key: "file-reviews", label: `File Reviews (${data.fileReviews?.length ?? 0})`, testId: "tab-cr-file-reviews" },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="sm"
          data-testid="button-back"
          onClick={() => setLocation("/compliance/credit-reps")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold" data-testid="cr-detail-name">
              {data.entityName}
            </h1>
            <CrStatusBadge status={data.crStatus} />
            <ComplianceStatusBadge status={data.complianceStatus} />
          </div>
          {data.crNumber && (
            <p className="text-sm text-muted-foreground font-mono">
              {data.crNumber}
            </p>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b">
        <nav className="flex gap-0" role="tablist" data-testid="cr-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              data-testid={tab.testId}
              data-active={activeTab === tab.key ? "true" : "false"}
              className={cn(
                "px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === tab.key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
              )}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div data-testid={`tab-content-${activeTab}`}>
        {activeTab === "profile" && (
          <ProfileTab cr={data} onEdit={() => setIsEditModalOpen(true)} />
        )}
        {activeTab === "documents" && <DocumentsTab cr={data} />}
        {activeTab === "file-reviews" && <FileReviewsTab cr={data} />}
      </div>

      {/* Edit CR Modal */}
      <CreditRepModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        editingCr={data}
      />
    </div>
  );
}

export default function CreditRepDetailPage() {
  return (
    <ComplianceLayout>
      <CreditRepDetailContent />
    </ComplianceLayout>
  );
}
